import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns'
import { pt } from 'date-fns/locale'
import { getAgendamentosRelatorio, getTamanhoPaginacao } from '../../lib/supabase'
import { valoresAgendamento, estadoParcelas, fmtEur, labelMetodo, METODO_STRIPE } from '../../lib/valores'
import { useTenant } from '../../contexts/TenantContext'
import { useToast } from '../../contexts/ToastContext'
import { useAuth } from '../../contexts/AuthContext'
import { ESTADOS, ORDEM_ESTADOS } from '../../lib/estados'
import Loading from '../../components/Loading'

const fmtData = (d) => format(d, 'yyyy-MM-dd')

export default function Relatorios() {
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const toast = useToast()
  const { estadosVisiveisRelatorio } = useAuth()
  const hoje = new Date()

  // Estados que este perfil pode filtrar (na ordem canónica).
  const estadosPermitidos = useMemo(
    () => ORDEM_ESTADOS.filter(e => estadosVisiveisRelatorio.has(e)),
    [estadosVisiveisRelatorio]
  )

  const [inicio, setInicio] = useState(fmtData(hoje))
  const [fim, setFim] = useState(fmtData(hoje))
  const [linhas, setLinhas] = useState([])
  const [pagamentos, setPagamentos] = useState([])
  const [carregando, setCarregando] = useState(false)
  // Filtros de estado — por defeito conta o que é receita (confirmado/concluído),
  // mas apenas entre os estados que o perfil pode filtrar.
  const [estadoSel, setEstadoSel] = useState({})
  const [paginaDetalhe, setPaginaDetalhe] = useState(1)
  const [tamanhoPaginaDetalhe, setTamanhoPaginaDetalhe] = useState(20)

  // Inicializa/ajusta os filtros aos estados permitidos ao perfil.
  useEffect(() => {
    setEstadoSel(prev => {
      const next = {}
      for (const e of estadosPermitidos) {
        next[e] = e in prev ? prev[e] : (e === 'confirmado' || e === 'concluido')
      }
      return next
    })
  }, [estadosPermitidos])

  useEffect(() => {
    if (!tenant) return
    setCarregando(true)
    const inicioISO = new Date(inicio + 'T00:00:00').toISOString()
    const fimISO = new Date(fim + 'T23:59:59').toISOString()
    getAgendamentosRelatorio(tenant.id, inicioISO, fimISO)
      .then(({ agendamentos, pagamentos }) => { setLinhas(agendamentos); setPagamentos(pagamentos) })
      .catch(e => toast.erro(e.message))
      .finally(() => setCarregando(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, inicio, fim])

  useEffect(() => {
    if (!tenant) return
    getTamanhoPaginacao(tenant.id).then(setTamanhoPaginaDetalhe).catch(() => {})
  }, [tenant])

  function presetHoje() { const d = new Date(); setInicio(fmtData(d)); setFim(fmtData(d)) }
  function presetSemana() {
    const d = new Date()
    setInicio(fmtData(startOfWeek(d, { weekStartsOn: 1 })))
    setFim(fmtData(endOfWeek(d, { weekStartsOn: 1 })))
  }
  function presetMes() {
    const d = new Date()
    setInicio(fmtData(startOfMonth(d)))
    setFim(fmtData(endOfMonth(d)))
  }
  function toggleEstado(e) { setEstadoSel(p => ({ ...p, [e]: !p[e] })) }

  // Limpa as classes de impressão quando a caixa de impressão fecha.
  useEffect(() => {
    const limpar = () => document.body.classList.remove('print-totais', 'print-detalhe')
    window.addEventListener('afterprint', limpar)
    return () => window.removeEventListener('afterprint', limpar)
  }, [])

  function imprimirPdf(tipo) {
    document.body.classList.remove('print-totais', 'print-detalhe')
    document.body.classList.add(tipo === 'detalhe' ? 'print-detalhe' : 'print-totais')
    window.print()
  }

  // Linhas visíveis ao perfil (só estados que pode filtrar).
  const linhasPermitidas = useMemo(
    () => linhas.filter(l => estadosVisiveisRelatorio.has(l.estado)),
    [linhas, estadosVisiveisRelatorio]
  )

  // Linhas que respeitam os filtros de estado escolhidos.
  const filtradas = useMemo(() => linhasPermitidas.filter(l => estadoSel[l.estado]), [linhasPermitidas, estadoSel])

  // Reinicia a paginação sempre que os dados, os filtros ou o tamanho da página mudam.
  useEffect(() => { setPaginaDetalhe(1) }, [filtradas, tamanhoPaginaDetalhe])

  const totalPaginasDetalhe = Math.max(1, Math.ceil(filtradas.length / tamanhoPaginaDetalhe))
  const paginaDetalheAtual = Math.min(paginaDetalhe, totalPaginasDetalhe)
  const detalhePagina = useMemo(() => {
    const inicioIdx = (paginaDetalheAtual - 1) * tamanhoPaginaDetalhe
    return filtradas.slice(inicioIdx, inicioIdx + tamanhoPaginaDetalhe)
  }, [filtradas, paginaDetalheAtual, tamanhoPaginaDetalhe])

  // Resumo dos filtros selecionados.
  const resumo = useMemo(() => {
    let total = 0, sinal = 0, restante = 0, recebido = 0, porReceber = 0
    for (const l of filtradas) {
      const v = valoresAgendamento(l, pagamentos)
      total += v.total; sinal += v.sinal; restante += v.restante
      recebido += v.recebido; porReceber += v.porReceber
    }
    const n = filtradas.length
    return { n, total, sinal, restante, recebido, porReceber }
  }, [filtradas, pagamentos])

  // Repartição por estado (todo o período, independente do filtro), só estados permitidos.
  const porEstado = useMemo(() => {
    const m = {}
    for (const e of estadosPermitidos) m[e] = { n: 0, total: 0 }
    for (const l of linhasPermitidas) {
      if (!m[l.estado]) m[l.estado] = { n: 0, total: 0 }
      m[l.estado].n += 1
      m[l.estado].total += valoresAgendamento(l, pagamentos).total
    }
    return m
  }, [linhasPermitidas, estadosPermitidos, pagamentos])

  // Tesouraria — como entrou o dinheiro recebido, por método real de cada
  // parcela paga (sinal ou restante podem ter sido pagos por Stripe ou no
  // salão — não se pode assumir que o sinal é sempre Stripe).
  const tesouraria = useMemo(() => {
    let stripe = 0
    const porMetodo = {}
    for (const l of filtradas) {
      const { sinal, restante } = estadoParcelas(l, pagamentos)
      for (const p of [sinal, restante]) {
        if (!p.pago) continue
        if (p.origem === 'stripe') {
          stripe += p.valor
        } else {
          const m = p.metodo || 'outro'
          porMetodo[m] = (porMetodo[m] || 0) + p.valor
        }
      }
    }
    return { stripe, porMetodo }
  }, [filtradas, pagamentos])

  // Repartição por profissional (respeita os filtros).
  const porProf = useMemo(() => {
    const m = new Map()
    for (const l of filtradas) {
      const nome = l.profissionais?.nome || '—'
      const v = valoresAgendamento(l, pagamentos)
      const a = m.get(nome) || { n: 0, total: 0, sinal: 0, restante: 0 }
      a.n += 1; a.total += v.total; a.sinal += v.sinal; a.restante += v.restante
      m.set(nome, a)
    }
    return [...m.entries()].sort((a, b) => b[1].total - a[1].total)
  }, [filtradas, pagamentos])

  const periodoLabel = inicio === fim
    ? format(new Date(inicio + 'T12:00:00'), "d 'de' MMMM 'de' yyyy", { locale: pt })
    : `${format(new Date(inicio + 'T12:00:00'), 'd MMM yyyy', { locale: pt })} — ${format(new Date(fim + 'T12:00:00'), 'd MMM yyyy', { locale: pt })}`

  return (
    <div className="relatorio" style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 24px 64px' }}>
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '8px' }}>
        <h2 style={{ fontSize: '22px' }}>Relatórios</h2>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={() => imprimirPdf('totais')} className="btn-secundario" style={{ padding: '9px 16px', fontSize: '14px' }}>
            🖨️ PDF Totais
          </button>
          <button onClick={() => imprimirPdf('detalhe')} className="btn-secundario" style={{ padding: '9px 16px', fontSize: '14px' }}>
            🖨️ PDF Detalhe
          </button>
        </div>
      </div>

      {/* Cabeçalho só visível na impressão */}
      <div className="apenas-print" style={{ display: 'none', marginBottom: '12px' }}>
        <h2 style={{ fontSize: '20px' }}>
          {tenant?.nome} — Relatório de marcações
          <span className="titulo-totais"> (Totais)</span>
          <span className="titulo-detalhe"> (Detalhe)</span>
        </h2>
        <p style={{ color: '#555', fontSize: '13px' }}>{periodoLabel}</p>
      </div>

      {/* Controlos */}
      <div className="no-print" style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', padding: '18px 20px', marginBottom: '24px', boxShadow: 'var(--sombra)' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <button onClick={presetHoje} className="btn-secundario" style={{ padding: '7px 14px', fontSize: '13px' }}>Hoje</button>
          <button onClick={presetSemana} className="btn-secundario" style={{ padding: '7px 14px', fontSize: '13px' }}>Esta semana</button>
          <button onClick={presetMes} className="btn-secundario" style={{ padding: '7px 14px', fontSize: '13px' }}>Este mês</button>
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>De</label>
            <input type="date" value={inicio} max={fim} onChange={e => setInicio(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>Até</label>
            <input type="date" value={fim} min={inicio} onChange={e => setFim(e.target.value)} style={inputStyle} />
          </div>
        </div>
        <div>
          <span style={{ fontSize: '13px', fontWeight: '500', marginRight: '4px' }}>Estados:</span>
          <div style={{ display: 'inline-flex', gap: '14px', flexWrap: 'wrap', marginTop: '6px' }}>
            {estadosPermitidos.map(e => (
              <label key={e} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!estadoSel[e]} onChange={() => toggleEstado(e)} style={{ width: '16px', height: '16px', accentColor: ESTADOS[e].cor }} />
                <span style={{ color: ESTADOS[e].cor, fontWeight: '600' }}>{ESTADOS[e].label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {carregando ? <Loading /> : (
        <>
          <div className="bloco-totais">
          {/* Cartões de resumo */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
            <Cartao titulo="Marcações" valor={resumo.n} />
            <Cartao titulo="Total" valor={fmtEur(resumo.total)} destaque />
            <Cartao titulo="Recebido" valor={fmtEur(resumo.recebido)} />
            <Cartao titulo="Por receber" valor={fmtEur(resumo.porReceber)} />
            <Cartao titulo="Sinal" valor={fmtEur(resumo.sinal)} />
            <Cartao titulo="Restante" valor={fmtEur(resumo.restante)} />
          </div>

          {/* Por estado */}
          <Bloco titulo="Por estado (no período)">
            <table style={tabela}>
              <thead><tr style={trHead}><th style={thL}>Estado</th><th style={thR}>Nº</th><th style={thR}>Total</th></tr></thead>
              <tbody>
                {estadosPermitidos.map(e => (
                  <tr key={e} style={trBody}>
                    <td style={tdL}><span style={{ color: ESTADOS[e].cor, fontWeight: '600' }}>{ESTADOS[e].label}</span></td>
                    <td style={tdR}>{porEstado[e]?.n || 0}</td>
                    <td style={tdR}>{fmtEur(porEstado[e]?.total || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Bloco>

          {/* Tesouraria — recebido por método */}
          <Bloco titulo="Recebido por método (filtros aplicados)">
            <table style={tabela}>
              <thead><tr style={trHead}><th style={thL}>Método</th><th style={thR}>Valor</th></tr></thead>
              <tbody>
                <tr style={trBody}>
                  <td style={tdL}>{labelMetodo(METODO_STRIPE)}</td>
                  <td style={tdR}>{fmtEur(tesouraria.stripe)}</td>
                </tr>
                {Object.entries(tesouraria.porMetodo).map(([m, valor]) => (
                  <tr key={m} style={trBody}>
                    <td style={tdL}>{labelMetodo(m)}</td>
                    <td style={tdR}>{fmtEur(valor)}</td>
                  </tr>
                ))}
                <tr style={{ background: 'var(--cor-creme)' }}>
                  <td style={{ ...tdL, fontWeight: '700' }}>Total recebido</td>
                  <td style={{ ...tdR, fontWeight: '700' }}>{fmtEur(resumo.recebido)}</td>
                </tr>
              </tbody>
            </table>
          </Bloco>

          {/* Por profissional */}
          {porProf.length > 0 && (
            <Bloco titulo="Por profissional (filtros aplicados)">
              <table style={tabela}>
                <thead><tr style={trHead}><th style={thL}>Profissional</th><th style={thR}>Nº</th><th style={thR}>Sinal</th><th style={thR}>Restante</th><th style={thR}>Total</th></tr></thead>
                <tbody>
                  {porProf.map(([nome, a]) => (
                    <tr key={nome} style={trBody}>
                      <td style={tdL}>{nome}</td>
                      <td style={tdR}>{a.n}</td>
                      <td style={tdR}>{fmtEur(a.sinal)}</td>
                      <td style={tdR}>{fmtEur(a.restante)}</td>
                      <td style={{ ...tdR, fontWeight: '700' }}>{fmtEur(a.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Bloco>
          )}

          </div>{/* fim bloco-totais */}

          {/* Detalhe */}
          <div className="bloco-detalhe">
          <Bloco titulo={`Detalhe (${filtradas.length})`}>
            {filtradas.length === 0 ? (
              <p style={{ color: 'var(--cor-texto-suave)', padding: '16px', textAlign: 'center' }}>Sem marcações para os filtros selecionados.</p>
            ) : (
              <>
                {/* Na impressão mostra-se a tabela completa (todas as linhas filtradas). */}
                <table style={tabela} className="tabela-detalhe-completa apenas-print">
                  <thead><tr style={trHead}><th style={thL}>Data</th><th style={thL}>Cliente</th><th style={thL}>Serviço</th><th style={thL}>Profissional</th><th style={thL}>Estado</th><th style={thR}>Sinal</th><th style={thR}>Restante</th><th style={thR}>Total</th></tr></thead>
                  <tbody>
                    {filtradas.map(l => {
                      const v = valoresAgendamento(l, pagamentos)
                      const d = new Date(l.data_hora_inicio)
                      return (
                        <tr key={l.id} style={trBody}>
                          <td style={tdL}>{format(d, 'dd/MM HH:mm')}</td>
                          <td style={tdL}>{l.cliente_nome || '—'}</td>
                          <td style={tdL}>{l.servicos?.nome || '—'}</td>
                          <td style={tdL}>{l.profissionais?.nome || '—'}</td>
                          <td style={tdL}><span style={{ color: (ESTADOS[l.estado]?.cor) || '#555', fontWeight: '600' }}>{ESTADOS[l.estado]?.label || l.estado}</span></td>
                          <td style={tdR}>{fmtEur(v.sinal)}</td>
                          <td style={tdR}>{fmtEur(v.restante)}</td>
                          <td style={{ ...tdR, fontWeight: '700' }}>{fmtEur(v.total)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                {/* Tabela paginada (ecrã). */}
                <table style={tabela} className="tabela-detalhe-paginada no-print">
                  <thead><tr style={trHead}><th style={thL}>Data</th><th style={thL}>Cliente</th><th style={thL}>Serviço</th><th style={thL}>Profissional</th><th style={thL}>Estado</th><th style={thR}>Sinal</th><th style={thR}>Restante</th><th style={thR}>Total</th></tr></thead>
                  <tbody>
                    {detalhePagina.map(l => {
                      const v = valoresAgendamento(l, pagamentos)
                      const d = new Date(l.data_hora_inicio)
                      return (
                        <tr
                          key={l.id}
                          style={{ ...trBody, cursor: 'pointer' }}
                          onClick={() => navigate(`/admin/agendamento/${l.id}`)}
                          title="Ver detalhe da marcação"
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--cor-creme)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}
                        >
                          <td style={tdL}>{format(d, 'dd/MM HH:mm')}</td>
                          <td style={tdL}>{l.cliente_nome || '—'}</td>
                          <td style={tdL}>{l.servicos?.nome || '—'}</td>
                          <td style={tdL}>{l.profissionais?.nome || '—'}</td>
                          <td style={tdL}><span style={{ color: (ESTADOS[l.estado]?.cor) || '#555', fontWeight: '600' }}>{ESTADOS[l.estado]?.label || l.estado}</span></td>
                          <td style={tdR}>{fmtEur(v.sinal)}</td>
                          <td style={tdR}>{fmtEur(v.restante)}</td>
                          <td style={{ ...tdR, fontWeight: '700' }}>{fmtEur(v.total)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                {totalPaginasDetalhe > 1 && (
                  <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '12px' }}>
                    <button
                      onClick={() => setPaginaDetalhe(p => Math.max(1, p - 1))}
                      disabled={paginaDetalheAtual === 1}
                      className="btn-secundario"
                      style={{ padding: '6px 12px', fontSize: '13px', opacity: paginaDetalheAtual === 1 ? 0.5 : 1 }}
                    >
                      ← Anterior
                    </button>
                    <span style={{ fontSize: '13px', color: 'var(--cor-texto-suave)' }}>
                      Página {paginaDetalheAtual} de {totalPaginasDetalhe}
                    </span>
                    <button
                      onClick={() => setPaginaDetalhe(p => Math.min(totalPaginasDetalhe, p + 1))}
                      disabled={paginaDetalheAtual === totalPaginasDetalhe}
                      className="btn-secundario"
                      style={{ padding: '6px 12px', fontSize: '13px', opacity: paginaDetalheAtual === totalPaginasDetalhe ? 0.5 : 1 }}
                    >
                      Seguinte →
                    </button>
                  </div>
                )}
              </>
            )}
          </Bloco>
          </div>{/* fim bloco-detalhe */}
        </>
      )}
    </div>
  )
}

const inputStyle = { padding: '9px 12px', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio)', fontSize: '14px', fontFamily: 'var(--font-ui)', background: 'var(--cor-branco)', color: 'var(--cor-texto)' }
const tabela = { width: '100%', borderCollapse: 'collapse', fontSize: '13px' }
const trHead = { background: 'var(--cor-creme)', borderBottom: '1px solid var(--cor-borda)' }
const trBody = { borderBottom: '1px solid var(--cor-borda)' }
const thL = { padding: '10px 12px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--cor-texto-suave)' }
const thR = { ...thL, textAlign: 'right' }
const tdL = { padding: '9px 12px', textAlign: 'left' }
const tdR = { padding: '9px 12px', textAlign: 'right' }

function Cartao({ titulo, valor, destaque }) {
  return (
    <div style={{ background: destaque ? 'var(--cor-principal)' : 'var(--cor-branco)', color: destaque ? '#fff' : 'var(--cor-texto)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', padding: '16px', boxShadow: 'var(--sombra)' }}>
      <div style={{ fontSize: '12px', opacity: 0.75, marginBottom: '6px' }}>{titulo}</div>
      <div style={{ fontSize: '22px', fontWeight: '700' }}>{valor}</div>
    </div>
  )
}

function Bloco({ titulo, children }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <p style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>{titulo}</p>
      <div style={{ background: 'var(--cor-branco)', border: '1px solid var(--cor-borda)', borderRadius: 'var(--raio-lg)', overflow: 'hidden', boxShadow: 'var(--sombra)' }}>
        {children}
      </div>
    </div>
  )
}
